import {
  Badge,
  Box,
  Flex,
  HStack,
  Link,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import {useContext, useState} from "react";
import {FiCode, FiFilm, FiGrid, FiSliders, FiType} from "react-icons/fi";
import {AdapterContext, ConnectionContext} from "./Contexts";
import {CommandCenter} from "./tabs/CommandCenter";
import {Effects} from "./tabs/Effects";
import {PixelAnimations} from "./tabs/PixelAnimations";
import {PixelScreen} from "./tabs/PixelScreen";
import {TextMode} from "./tabs/TextMode";

const pages = [
  {path: "/pixel", name: "Canvas", code: "Draw", color: "#ffd84d", icon: FiGrid, element: PixelScreen},
  {path: "/animation", name: "Animation", code: "Motion", color: "#ff7a68", icon: FiFilm, element: PixelAnimations},
  {path: "/text", name: "Text", code: "Type", color: "#5ed5c8", icon: FiType, element: TextMode},
  {path: "/effects", name: "Device", code: "Control", color: "#8bb7ff", icon: FiSliders, element: Effects},
  {path: "/commands", name: "Commands", code: "Protocol", color: "#c5a3ff", icon: FiCode, element: CommandCenter},
];

export function TabsPage(props) {
  const adapter = useContext(AdapterContext);
  const connected = useContext(ConnectionContext);
  const orientation = useBreakpointValue({base: "horizontal", lg: "vertical"}) ?? "horizontal";
  const [activeIndex, setActiveIndex] = useState(0);
  const activePage = pages[activeIndex];
  const profile = adapter.getDisplayProfile();

  return (
    <Tabs
      index={activeIndex}
      onChange={setActiveIndex}
      orientation={orientation}
      variant="unstyled"
      display="grid"
      gridTemplateColumns={{base: "minmax(0, 1fr)", lg: "208px minmax(0, 1fr)"}}
      minH={{base: "calc(100vh - 105px)", lg: "calc(100vh - 76px)"}}
      borderLeft={{base: 0, md: "1px solid"}}
      borderRight={{base: 0, md: "1px solid"}}
      borderColor="#24272b"
      bg="#0b0c0e"
    >
      <Flex
        as="nav"
        direction="column"
        borderRight={{base: 0, lg: "1px solid"}}
        borderBottom={{base: "1px solid", lg: 0}}
        borderColor="#24272b"
        bg="#0e1012"
        minW={0}
      >
        <Box px={5} pt={6} pb={3} display={{base: "none", lg: "block"}}>
          <Text color="#666c74" fontSize="10px" fontWeight="800" textTransform="uppercase">Workspace</Text>
        </Box>
        <TabList
          display={{base: "grid", lg: "flex"}}
          gridTemplateColumns={{base: "repeat(5, minmax(0, 1fr))", sm: "repeat(5, auto)"}}
          flexDirection={{base: "row", lg: "column"}}
          gap={{base: 0, lg: 1}}
          px={{base: 2, lg: 3}}
          py={{base: 2, lg: 0}}
          overflowX="auto"
          width="100%"
        >
          {pages.map((page) => (
            <Tab
              key={page.path}
              aria-label={`${page.name} workspace`}
              title={`${page.name} workspace`}
              minW={0}
              minH={{base: "48px", lg: "52px"}}
              px={{base: 2, lg: 3}}
              py={2}
              borderRadius="5px"
              color="#747a83"
              justifyContent={{base: "center", sm: "flex-start"}}
              _selected={{
                bg: "#1a1d20",
                color: "#f4f5f6",
                "& .mode-icon": {color: page.color, borderColor: `${page.color}66`, bg: `${page.color}12`},
              }}
              _hover={{bg: "#151719", color: "#d8dade"}}
            >
              <HStack spacing={{base: 0, sm: 3}} minW={0} width="100%">
                <Flex
                  className="mode-icon"
                  width="32px"
                  height="32px"
                  align="center"
                  justify="center"
                  border="1px solid transparent"
                  borderRadius="4px"
                  flexShrink={0}
                >
                  <page.icon size="15px" />
                </Flex>
                <Box display={{base: "none", sm: "block"}} minW={0} textAlign="left">
                  <Text fontSize="sm" lineHeight="1.1" fontWeight="700" noOfLines={1}>{page.name}</Text>
                  <Text mt="3px" fontSize="9px" color="#626870" textTransform="uppercase" fontWeight="700">{page.code}</Text>
                </Box>
              </HStack>
            </Tab>
          ))}
        </TabList>

        <Box mt="auto" px={5} py={5} borderTop="1px solid" borderColor="#24272b" display={{base: "none", lg: "block"}}>
          <HStack justify="space-between" mb={2}>
            <Text color="#666c74" fontSize="9px" textTransform="uppercase" fontWeight="800">Adapter</Text>
            <Box width="6px" height="6px" borderRadius="full" bg={connected ? "#5ed5a8" : "#5d6269"} />
          </HStack>
          <Text fontSize="sm" fontWeight="700">{adapter.brandName}</Text>
          <Text mt={1} color="#6d737b" fontSize="10px">{profile.name} · {profile.width} × {profile.height}</Text>
          <Link
            href="https://github.com/iebb/WebSGuai/blob/master/docs/ADAPTERS.md"
            isExternal
            display="inline-block"
            mt={4}
            color="#7f858e"
            fontSize="10px"
            _hover={{color: "#d8dade"}}
          >
            Adapter reference
          </Link>
        </Box>
      </Flex>

      <Box minW={0} bg="#0b0c0e">
        <Flex
          minH={{base: "64px", md: "76px"}}
          px={{base: 4, md: 6, xl: 8}}
          py={3}
          align="center"
          justify="space-between"
          gap={4}
          borderBottom="1px solid"
          borderColor="#24272b"
        >
          <Box>
            <Text color={activePage.color} fontSize="9px" fontWeight="800" textTransform="uppercase">{activePage.code}</Text>
            <Text as="h2" mt="2px" fontSize={{base: "xl", md: "2xl"}} lineHeight="1" fontWeight="800">{activePage.name}</Text>
          </Box>
          <HStack spacing={2}>
            <Badge variant="outline" borderColor="#34383e" color="#8d939c" borderRadius="3px" fontSize="9px">
              {profile.width} × {profile.height}
            </Badge>
            <Badge colorScheme={connected ? "green" : "gray"} borderRadius="3px" fontSize="9px">
              {connected ? "Linked" : "Draft"}
            </Badge>
          </HStack>
        </Flex>

        <TabPanels>
          {pages.map((page) => (
            <TabPanel key={page.path} p={{base: 4, md: 6, xl: 8}} minH={{base: "540px", md: "680px"}}>
              <page.element {...props} />
            </TabPanel>
          ))}
        </TabPanels>
      </Box>
    </Tabs>
  );
}
